import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { loadBranded } from '../../utils/bootstrap';
import useHideOnScrollDown from '../../hooks/useHideOnScrollDown';
// Show/hide flag — see isWhatsAppButtonEnabled for why it is `!== false`
// rather than the usual isFeatureEnabled().
import { isWhatsAppButtonEnabled } from '../../utils/siteFeatures';

// Server-injected contact (window.__HM_BRAND__.contact) takes priority over
// localStorage so the WhatsApp button's phone is correct on first paint.
const load = (key, fb) => {
    if (key === 'hm_page_contact') return loadBranded(key, 'contact', fb);
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; }
};

const PAGE_MESSAGES = {
    '/deals':          "Hi! I saw your travel deals and I'm interested. Can you tell me more?",
    '/hajj-umrah':     "Hi! I'm interested in your Hajj/Umrah packages. Can you provide details and pricing?",
    '/hotels-partner': "Hi! I'd like to enquire about one of your hotel packages. Can you help?",
    '/hotels':         "Hi! I'm looking for hotel recommendations. Can you help me find the perfect stay?",
    '/flights':        "Hi! I need help searching for flights. Can you assist me?",
    '/visa':           "Hi! I need information about visa requirements for my trip. Can you help?",
    '/destinations':   "Hi! I'd like to learn more about your travel destinations.",
};

function WAIcon({ className }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}

/**
 * @param {boolean} primary  true when the AI chatbot FAB is NOT rendered —
 *   WhatsApp then takes the prime bottom corner itself (slightly larger,
 *   sitting above the mobile bottom nav via --mbn-height). When the chatbot
 *   IS present, WhatsApp keeps its stacked spot above it. Handles all four
 *   enable/disable combinations on both mobile and desktop.
 */
export default function WhatsAppButton({ primary = false }) {
    const location  = useLocation();
    const [open, setOpen]   = useState(false);
    // Phones: slide aside while the visitor scrolls down (it covered content).
    const tuckAway = useHideOnScrollDown();
    const [msg,  setMsg]    = useState('');
    const [phone, setPhone] = useState('');
    const [enabled, setEnabled] = useState(isWhatsAppButtonEnabled);

    /* Read phone number + the show/hide flag from site settings.
       Both arrive in the same 'hm-settings-loaded' broadcast, so an admin
       toggling this off takes effect on the next load without a rebuild. */
    useEffect(() => {
        const read = () => {
            const c = load('hm_page_contact', {});
            setPhone(c.whatsapp || c.phone || '');
            setEnabled(isWhatsAppButtonEnabled());
        };
        read();
        window.addEventListener('hm-settings-loaded', read);
        return () => window.removeEventListener('hm-settings-loaded', read);
    }, []);

    /* The phones' "Help" button (HelpLauncher) opens this chat window. */
    useEffect(() => {
        const openIt = () => setOpen(true);
        window.addEventListener('hm-open-whatsapp', openIt);
        return () => window.removeEventListener('hm-open-whatsapp', openIt);
    }, []);

    /* Pre-fill message based on current page */
    useEffect(() => {
        const path  = location.pathname;
        const match = Object.keys(PAGE_MESSAGES).find(k => path.startsWith(k));
        setMsg(match ? PAGE_MESSAGES[match] : "Hi! I'd like to enquire about your travel services.");
        setOpen(false); // close popup on navigation
    }, [location.pathname]);

    /* Switched off for this site in Features */
    if (!enabled) return null;

    /* Hide on admin / confirm-booking pages */
    const hiddenPaths = ['/private', '/confirm-booking'];
    if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;

    /* Strip phone to digits only (keep leading +) */
    const cleanPhone = phone.replace(/[\s\-()]/g, '').replace(/^\+/, '');
    if (!cleanPhone) return null;

    const openWhatsApp = () => {
        window.open(
            `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`,
            '_blank',
            'noopener,noreferrer'
        );
    };

    return (
        <>
            {/* Backdrop to close popup */}
            {open && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* ─── Chat popup ──────────────────────────────────── */}
            {open && (
                <div
                    className="fixed bottom-24 right-4 sm:right-6 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-700 overflow-hidden"
                    style={{ animation: 'waSlideUp 0.22s cubic-bezier(.23,1,.32,1)' }}
                    role="dialog"
                    aria-label="WhatsApp Chat"
                >
                    {/* Header */}
                    <div className="bg-[#075E54] px-4 py-3.5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0 shadow-sm">
                            <WAIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold text-sm leading-tight">Chat with Us</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                                <p className="text-green-200 text-xs">Online · Replies in minutes</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-white/70 hover:text-white transition-colors flex-shrink-0 p-2.5 -m-1.5 rounded-full hover:bg-white/10"
                            aria-label="Close chat"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* WhatsApp-style chat area */}
                    <div
                        className="px-4 py-4"
                        style={{
                            background: 'url("data:image/svg+xml,%3Csvg width=\'300\' height=\'300\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Crect width=\'300\' height=\'300\' fill=\'%23e5ddd5\'/%3E%3C/svg%3E")',
                            backgroundColor: '#E5DDD5',
                        }}
                    >
                        {/* Incoming bubble */}
                        <div className="flex gap-2 mb-1">
                            <div className="w-7 h-7 rounded-full bg-[#075E54] flex items-center justify-center flex-shrink-0 mt-1">
                                <WAIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div
                                className="bg-white dark:bg-slate-600 rounded-tr-2xl rounded-b-2xl px-3 py-2 shadow-sm"
                                style={{ maxWidth: '220px' }}
                            >
                                <p className="text-[11px] text-[#075E54] font-semibold mb-0.5">Support Team</p>
                                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                                    👋 Hello! How can we help you today?
                                </p>
                                <p className="text-[11px] md:text-[10px] text-slate-400 text-end mt-1">Just now</p>
                            </div>
                        </div>
                    </div>

                    {/* Message input */}
                    <div className="bg-[#F0F0F0] dark:bg-slate-800 px-3 py-2.5 flex items-end gap-2 border-t border-slate-200 dark:border-slate-700">
                        <textarea
                            value={msg}
                            onChange={e => setMsg(e.target.value)}
                            rows={2}
                            placeholder="Type a message…"
                            className="flex-1 resize-none text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-2xl px-3 py-2 outline-none shadow-sm border border-transparent focus:border-[#25D366] transition-colors placeholder-slate-400 leading-snug"
                        />
                        <button
                            onClick={openWhatsApp}
                            disabled={!msg.trim()}
                            className="w-10 h-10 rounded-full bg-[#075E54] hover:bg-[#054C41] disabled:bg-slate-300 text-white flex items-center justify-center flex-shrink-0 transition-colors shadow-sm"
                            aria-label="Open WhatsApp"
                        >
                            {/* Paper plane send icon */}
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 translate-x-0.5">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                        </button>
                    </div>

                    {/* Footer note */}
                    <div className="bg-white dark:bg-slate-800 px-4 py-2 flex items-center justify-center gap-1 border-t border-slate-100 dark:border-slate-700">
                        <WAIcon className="w-3 h-3 text-[#25D366]" />
                        <span className="text-[11px] text-slate-400">Continue on WhatsApp</span>
                    </div>
                </div>
            )}

            {/* ─── Floating button ─────────────────────────────── */}
            {/* primary (no chatbot): SAME mobile spot as always (bottom-24 —
                clear of the bottom nav, proven on live sites); on desktop it
                drops into the chatbot's corner. The --mbn-height calc tried
                first floated too high on live mobile — never again. */}
            {!open && <button
                onClick={() => setOpen(true)}
                data-wa-primary={primary ? 'true' : undefined}
                className={`fixed right-4 sm:right-6 z-50 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 group bg-[#25D366] hover:bg-[#20BA5A] hover:scale-110 ${primary ? 'bottom-24 sm:bottom-6 w-12 h-12 sm:w-14 sm:h-14' : 'bottom-24 w-12 h-12'}`}
                aria-label="Chat on WhatsApp"
                style={{ translate: tuckAway ? '160% 0' : '0 0', transition: 'translate 0.25s ease, transform 0.3s, box-shadow 0.3s, background-color 0.3s' }}
            >
                <>
                    <WAIcon className="w-6 h-6 text-white relative z-10" />
                    {/* Pulse animation ring */}
                    <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20 pointer-events-none hidden sm:block" />
                </>

                {/* Tooltip (desktop only) */}
                <span className="hidden sm:block absolute right-full mr-3 whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
                    Chat on WhatsApp
                    {/* Arrow */}
                    <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-slate-900" />
                </span>
            </button>}

            {/* Popup keyframe */}
            <style>{`
                @keyframes waSlideUp {
                    from { opacity: 0; transform: translateY(18px) scale(0.96); }
                    to   { opacity: 1; transform: translateY(0)     scale(1);   }
                }
            `}</style>
        </>
    );
}
